import React, { ReactElement, useCallback, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  Pagination,
  A11y,
  Swiper as SwiperInstance,
  SwiperOptions,
} from 'swiper'
import { Box, Theme } from '@mui/material'

import Breakpoints from '@config/theme/definitions/breakpoints'
import muiTheme from '@config/theme'
import { SliderUi } from './components/SliderArrows'

import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/a11y'
import { useIntl } from 'react-intl'

const scaleZoomProgress = (swiper: SwiperInstance, progress: number) => {
  const totalSlides = swiper.slides.length - 1
  const slideWidth = swiper.width
  const stepSize = 1 / totalSlides
  swiper.slides.forEach((slide: any, index) => {
    const slideProgress = (index / totalSlides - progress) / stepSize
    if (slideProgress < 0) {
      slide.style.opacity = 1 + slideProgress
      slide.style.transform = `
        translateX(${-slideProgress * slideWidth}px)
      `
    } else if (slideProgress >= 0) {
      slide.style.transform = 'translateX(0%) scale(1)'
      slide.style.width = `100%`
      slide.style.opacity = 1
    }
  })
}

const scaleZoomSetTransition = (swiper: SwiperInstance, transition: number) => {
  swiper.slides.forEach((slide: any) => {
    slide.style.transition = `width ${transition}ms ease, transform ${transition}ms ease, opacity ${transition}ms ease`
  })
}

type SliderBreakpointsType = {
  sm?: SwiperOptions
  md?: SwiperOptions
  lg?: SwiperOptions
}

export type SliderProps = MCDC.Props.IDefault & {
  theme?: MCDC.Props.ThemeType
  variant?: 'default' | 'custom'
  arrows?: 'overlay' | 'none'
  slidesPerView?: number | 'auto'
  initialSlide?: number
  onChangeSlide?: (activeIndex: number) => void
  withOverflow?: MCDC.Props.BreakpointFlags | boolean
  isLoop?: boolean
  isAutoHeight?: boolean
  breakpoints?: SliderBreakpointsType
  children: MCDC.Props.IDefault['children']
  sx?: MCDC.Props.IDefault['sx']
}
export default function Slider({
  theme,
  variant = 'default',
  arrows,
  slidesPerView = 1,
  isLoop = false,
  isAutoHeight = false,
  initialSlide = 0,
  breakpoints,
  onChangeSlide,
  children,
  sx,
  withOverflow,
}: SliderProps): ReactElement {
  const [, updateState] = useState<any>()
  const forceUpdate = useCallback(() => updateState({}), [])
  const slides = React.Children.toArray(children)
  const isDefaultTheme = !theme || theme !== 'primary'
  const isCustomVariant = variant === 'custom'
  const withOverflowBreakpoints: MCDC.Props.BreakpointFlags = {
    sm: !!withOverflow,
    md: !!withOverflow,
    lg: !!withOverflow,
    ...(typeof withOverflow === 'object' ? withOverflow : {}),
  }

  const additionalProps = isCustomVariant
    ? {
        // dir: 'rtl',
        onProgress: scaleZoomProgress,
        onSetTransition: scaleZoomSetTransition,
      }
    : {}

  const intl = useIntl()

  function getBreakpointOptions() {
    return Object.entries(breakpoints || {}).reduce(
      (obj: SliderBreakpointsType, [key, value]) => ({
        ...obj,
        [`${(Breakpoints?.values as any)?.[key]}`]: value as number,
      }),
      {}
    )
  }

  return (
    <Box
      sx={[
        sx,
        (theme: Theme) => ({
          position: 'relative',
          pb: 12,
          '> .swiper': {
            position: 'initial',
            overflow: withOverflowBreakpoints.sm ? 'visible' : undefined,
            [theme.breakpoints.up('md')]: {
              overflow: withOverflowBreakpoints.md ? 'visible' : 'hidden',
            },
            [theme.breakpoints.up('lg')]: {
              overflow: withOverflowBreakpoints.lg ? 'visible' : 'hidden',
            },
          },
          '.swiper': {
            '&-slide': {
              direction: 'ltr',
              visibility: 'visible',
              ':not(.swiper-slide-visible)': {
                visibility: 'hidden',
              },
            },
            '&-pagination': {
              bottom: '0 !important',
              direction: 'ltr',
              '&-bullet': {
                width: '24px',
                height: '24px',
                border: '6px solid transparent',
                backgroundColor: 'transparent',
                margin: '0px!important',
                opacity: 1,
                transition: 'width 0.3s ease',

                '&:focus-visible': {
                  outline: 'none',
                  position: 'relative',

                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: '-3px',
                    border: '2px solid blue',
                  },
                },

                '&:after': {
                  content: '""',
                  display: 'block',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'tertiary.main',
                  borderRadius: '6px',
                  transition: 'width 0.3s ease',
                },
                '&-active': {
                  width: '48px',
                  '&:after': {
                    content: '""',
                    display: 'block',
                    width: '36px',
                    height: '12px',
                    backgroundColor: 'secondary.main',
                    borderRadius: '6px',
                  },
                },
              },
            },
          },
        }),
      ]}
    >
      <Swiper
        speed={750}
        spaceBetween={16}
        touchReleaseOnEdges={true}
        loop={isLoop}
        autoHeight={isAutoHeight}
        observer={!!breakpoints}
        initialSlide={initialSlide}
        slidesPerView={slidesPerView}
        breakpoints={getBreakpointOptions()}
        onResize={() => {
          forceUpdate()
        }}
        onSlideChangeTransitionEnd={() => {
          forceUpdate()
        }}
        onSlideChange={(swiper: SwiperInstance) => {
          if (onChangeSlide) onChangeSlide(swiper.realIndex)
        }}
        modules={[A11y, ...(slides.length > 0 ? [Pagination] : [])]}
        pagination={{ clickable: true, bulletElement: 'button' }}
        a11y={{
          paginationBulletMessage: intl.formatMessage({
            id: 'label.sliderGoToSlide',
          }),
        }}
        watchSlidesProgress={true}
        {...additionalProps}
      >
        {arrows !== 'none' && (
          <span slot="container-start">
            <SliderUi
              loop={isLoop}
              theme={theme}
              sx={(theme: Theme) => ({
                mt: slides.length > 1 ? -6 : 0,
                [theme.breakpoints.down('md')]: { display: 'none' },
              })}
            />
          </span>
        )}
        {slides.map((child, index) => {
          return (
            <SwiperSlide key={(child as any).key || index}>{child}</SwiperSlide>
          )
        })}
      </Swiper>
    </Box>
  )
}
